import { assign, keys } from 'lodash';
import { Fetch } from '@trp-ta-nitro/http-js';
import AuthType, {
  RedirectInterceptor,
  TYPE_EXTERNAL,
  TYPE_INTERNAL,
  TYPES,
} from '../AuthType';
import * as Cookies from '../cookies';
import * as Domains from '../domains';
import * as Env from '../env/index';
import * as Errors from '../errors';
import * as Location from '../location';
import * as Storage from '../storage';
import { EnvironmentProvider, Type, TypeMapping } from '../types';
import { BaseAuthServiceOptions } from './BaseAuthService';
import ExternalAuthService, { ExternalAuthServiceOptions } from './ExternalAuthService';
import InternalAuthService, { InternalAuthServiceOptions } from './InternalAuthService';
import { AuthService } from './types';

function checkCustomerWebView(options: AuthServiceOptions) {
  if (
    options.customerWebViewEnabled &&
    !options.types.external &&
    !options.types.internal
  ) {
    throw new Error(
      'WHen customerWebViewEnabled, must include both external and internal auth types.',
    );
  }
}

// applies common options from passed configuration
function baseAuthOptions(
  options: AuthServiceOptions,
  authType: AuthType,
): BaseAuthServiceOptions {
  const environment = options.envProvider?.(
    // in practice the location will be there, but type enforcement complains
    options.location?.hostname ?? '',
    authType.typeMapping,
  );
  if (!environment) {
    throw new Error('Missing environment.');
  }
  checkCustomerWebView(options);
  return {
    apiKey: options.apiKey,
    authType,
    baseDomain: authType.typeMapping.domain,
    cookies: options.cookies ?? new Cookies.DocumentCookies(),
    customerWebViewEnabled: options.customerWebViewEnabled,
    environment,
    fetch: options.fetch,
    location: Location.checkedLocation(options.location),
    requireAuthentication: options.requireAuthentication,
    ...options.types[authType.typeMapping.type],
  };
}

// coerces base options into external options
function externalOptions(
  options: AuthServiceOptions,
  authType: AuthType,
): ExternalAuthServiceOptions {
  return {
    ...baseAuthOptions(options, authType),
    ...options.types.external,
  };
}

// coerces base options into internal options
function internalOptions(
  options: AuthServiceOptions,
  authType: AuthType,
): InternalAuthServiceOptions {
  return {
    ...baseAuthOptions(options, authType),
    ...options.types.internal,
  };
}

export interface AuthServiceHooks {
  // a function that is called when redirecting to another auth type, this will
  // be passed the type mapping the user is supposed to be using and the url
  // they are being redirect to
  // - if a url is returned, that url will be used to redirect instead
  // - if null is returned, the redirect doesn't happen
  authTypeRedirectInterceptor?: RedirectInterceptor;
}

export interface AuthServiceOptions {
  // an apigee API key for interacting with the various auth endpoints, this is
  // the only required parameter to instantiate an auth service
  apiKey: string;
  // a full set of type mappings for all auth types, defining how to recognize
  // them, their domain, and type
  //
  // it is expected that this type mapping is static and always configured for
  // all potential auth types, even if they are not available for an individual
  // usage...otherwise behaviors like auto-redirect and customerWebView will not
  // work
  authTypeMappings?: TypeMapping[];
  // passed in Cookies interaction, not meant to be overridden, but an explicit dependency to facilitate testing
  cookies?: Cookies.Cookies;
  // whether the CustomerWebView functionality is enabled or not
  customerWebViewEnabled?: boolean;
  // a function that returns the current environment...defaults to using the page's
  // domain and TRP convention to figure out the domain
  envProvider?: EnvironmentProvider;
  fetch?: Fetch<RequestInit, Response>;
  // a set of overridable/pluggable function-based behavior, see their type
  // definition for more details
  hooks?: AuthServiceHooks;
  // passed in Location, not meant to be overridden, but an explicit dependency to facilitate testing
  location?: Location;
  // pass true to automatically redirect to the expected url for a supported
  // type if the current url is not supported
  redirectOnMismatchedAuthType?: boolean;
  // whether the user will be forced to login if they are not authenticated or not
  requireAuthentication?: boolean;
  // passed in Storage, not meant to be overridden, but an explicit dependency to facilitate testing
  storage?: Storage;
  // the supported auth types to instantiate...if one of these is not passed it
  // will NOT be considered supported
  //   ex: types: { external: {} } will enable external but not internal auth
  //
  // each auth service has its own set of options that are overridable if necessary
  types: {
    external?: Partial<ExternalAuthServiceOptions>;
    internal?: Partial<InternalAuthServiceOptions>;
  };
}

const DEFAULT_OPTIONS: Partial<AuthServiceOptions> = {
  authTypeMappings: [
    {
      domain: Domains.EXTERNAL_DOMAIN,
      hostRegex: Domains.regexSubdomainsOf(Domains.EXTERNAL_DOMAIN),
      type: 'external',
    },
    {
      domain: Domains.INTERNAL_DOMAIN,
      hostRegex: Domains.regexSubdomainsOf(Domains.INTERNAL_DOMAIN),
      type: 'internal',
    },
  ],
  customerWebViewEnabled: false,
  envProvider: Env.environmentFromDomain,
  location: Location.windowLocation(),
  redirectOnMismatchedAuthType: true,
  requireAuthentication: true,
  storage: Storage.localStorage(),
};

/**
 * This is the primary entry point for using the auth service. It uses the
 * options it is passed, combined with internal defaults, to determine the
 * type of auth that needs to occur (internal or external) from the context
 * of the current page. It then instantiates the proper auth service and
 * returns it.
 *
 * @param options See type definition for description.
 */
export function newAuthService(options: AuthServiceOptions): AuthService {
  options = assign({}, DEFAULT_OPTIONS, options);
  const typeMappings = options.authTypeMappings;
  if (!typeMappings) {
    throw new Error('Missing typeMappings');
  }

  const authType = new AuthType({
    location: Location.checkedLocation(options.location),
    redirectInterceptor: options.hooks?.authTypeRedirectInterceptor,
    redirectOnUnsupportedType: options.redirectOnMismatchedAuthType,
    storage: options.storage,
    supportedTypes: keys(options.types) as Type[],
    typeMappings,
  });
  switch (authType.typeMapping.type) {
    case TYPE_EXTERNAL:
      return new ExternalAuthService(externalOptions(options, authType));
    case TYPE_INTERNAL:
      return new InternalAuthService(internalOptions(options, authType));
    default:
      throw new Errors.UnknownAuthTypeError(authType.typeMapping.type, TYPES);
  }
}
